/**
 * WCAG 2.1 Accessibility Utilities for TraderTekno
 * Comprehensive keyboard navigation and screen reader support
 */

import { logger } from './logger';

// Keyboard navigation handler
export const handleKeyNavigation = (
  event: KeyboardEvent,
  action: () => void,
  options: {
    preventSpaceDefault?: boolean;
    preventDefault?: boolean;
  } = {}
) => {
  const { preventSpaceDefault = true, preventDefault = true } = options;
  
  if (event.key === 'Enter') {
    if (preventDefault) {
      event.preventDefault();
    }
    action();
  } else if (event.key === ' ') {
    if (preventSpaceDefault) {
      event.preventDefault();
    }
    action();
  }
};

// Focus management utilities
export class FocusManager {
  private static focusableElements = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[role="button"]:not([disabled])',
    '[role="link"]',
    '[role="menuitem"]'
  ].join(', ');

  static getFocusableElements(container: HTMLElement): HTMLElement[] {
    return Array.from(container.querySelectorAll(this.focusableElements));
  }

  static trapFocus(container: HTMLElement) {
    const focusableElements = this.getFocusableElements(container);
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') {
        return;
      }

      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement?.focus();
        }
      }
    };

    container.addEventListener('keydown', handleTabKey);
    firstElement?.focus();

    return () => container.removeEventListener('keydown', handleTabKey);
  }

  static restoreFocus(element: HTMLElement | null) {
    if (element?.focus) {
      element.focus();
    }
  }
}

// Screen reader announcements
export class ScreenReaderAnnouncer {
  private static liveRegion: HTMLElement | null = null;

  static init() {
    if (this.liveRegion) {
      return;
    }

    this.liveRegion = document.createElement('div');
    this.liveRegion.setAttribute('aria-live', 'polite');
    this.liveRegion.setAttribute('aria-atomic', 'true');
    this.liveRegion.className = 'sr-only';
    this.liveRegion.id = 'screen-reader-announcements';
    document.body.appendChild(this.liveRegion);
  }

  static announce(message: string, priority: 'polite' | 'assertive' = 'polite') {
    this.init();
    if (!this.liveRegion) {
      return;
    }

    this.liveRegion.setAttribute('aria-live', priority);
    this.liveRegion.textContent = message;

    // Clear after announcement
    setTimeout(() => {
      if (this.liveRegion) {
        this.liveRegion.textContent = '';
      }
    }, 1000);
  }

  static announceSignalUpdate(signal: {
    instrument: string;
    direction: string;
    confidence: number;
  }) {
    this.announce(
      `New trading signal: ${signal.direction} ${signal.instrument} with ${signal.confidence}% confidence`,
      'polite'
    );
  }

  static announceError(error: string) {
    this.announce(`Error: ${error}`, 'assertive');
  }

  static announceSuccess(message: string) {
    this.announce(`Success: ${message}`, 'polite');
  }
}

// Color contrast utilities
export const ColorContrast = {
  // Check if color combination meets WCAG 2.1 AA standards (4.5:1 ratio)
  meetsWCAG_AA: (foreground: string, background: string): boolean => {
    const ratio = ColorContrast.getContrastRatio(foreground, background);
    return ratio >= 4.5;
  },

  // Check if color combination meets WCAG 2.1 AAA standards (7:1 ratio)
  meetsWCAG_AAA: (foreground: string, background: string): boolean => {
    const ratio = ColorContrast.getContrastRatio(foreground, background);
    return ratio >= 7;
  },

  // Calculate contrast ratio between two colors
  getContrastRatio: (color1: string, color2: string): number => {
    const l1 = ColorContrast.getLuminance(color1);
    const l2 = ColorContrast.getLuminance(color2);
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    return (lighter + 0.05) / (darker + 0.05);
  },

  // Get relative luminance of a color
  getLuminance: (color: string): number => {
    const rgb = ColorContrast.hexToRgb(color);
    if (!rgb) {
      return 0;
    }

    const [r, g, b] = [rgb.r, rgb.g, rgb.b].map(c => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  },

  // Convert hex color to RGB
  hexToRgb: (hex: string): { r: number; g: number; b: number } | null => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  }
};

// Touch target utilities
export const TouchTargets = {
  MIN_SIZE: 44, // WCAG minimum touch target size in pixels

  isValidSize: (element: HTMLElement): boolean => {
    const rect = element.getBoundingClientRect();
    return rect.width >= TouchTargets.MIN_SIZE && rect.height >= TouchTargets.MIN_SIZE;
  },

  validateAllTouchTargets: (): HTMLElement[] => {
    const interactiveElements = document.querySelectorAll(
      'button, a, input, select, textarea, [role="button"], [tabindex]'
    );
    
    const invalidElements: HTMLElement[] = [];
    
    interactiveElements.forEach(element => {
      if (!TouchTargets.isValidSize(element as HTMLElement)) {
        invalidElements.push(element as HTMLElement);
      }
    });

    return invalidElements;
  }
};

// Accessibility testing utilities
export const AccessibilityTester = {
  // Test all accessibility features
  runCompleteTest: (): {
    passed: boolean;
    results: {
      focusManagement: boolean;
      touchTargets: boolean;
      ariaLabels: boolean;
      semanticHTML: boolean;
    };
    issues: string[];
  } => {
    const results = {
      focusManagement: AccessibilityTester.testFocusManagement(),
      touchTargets: AccessibilityTester.testTouchTargets(),
      ariaLabels: AccessibilityTester.testAriaLabels(),
      semanticHTML: AccessibilityTester.testSemanticHTML()
    };

    const issues: string[] = [];
    
    if (!results.focusManagement) {
      issues.push('Focus management issues detected');
    }
    if (!results.touchTargets) {
      issues.push('Touch target size violations found');
    }
    if (!results.ariaLabels) {
      issues.push('Missing ARIA labels detected');
    }
    if (!results.semanticHTML) {
      issues.push('Semantic HTML structure issues found');
    }

    return {
      passed: Object.values(results).every(result => result),
      results,
      issues
    };
  },

  testFocusManagement: (): boolean => {
    const focusableElements = document.querySelectorAll(
      'a, button, input, select, textarea, [tabindex], [role="button"]'
    );
    
    const passed = true;
    
    focusableElements.forEach(element => {
      const tabIndex = element.getAttribute('tabindex');
      if (tabIndex === '-1' && !element.hasAttribute('aria-hidden')) {
        // Element is not focusable but might need to be
        console.warn('Element is not focusable:', element);
      }
    });

    return passed;
  },

  testTouchTargets: (): boolean => {
    const invalidElements = TouchTargets.validateAllTouchTargets();
    
    if (invalidElements.length > 0) {
      console.warn('Touch target violations:', invalidElements);
      return false;
    }
    
    return true;
  },

  testAriaLabels: (): boolean => {
    const interactiveElements = document.querySelectorAll(
      'button, a, input, select, textarea, [role="button"]'
    );
    
    let passed = true;
    
    interactiveElements.forEach(element => {
      const hasLabel = element.hasAttribute('aria-label') || 
                      element.hasAttribute('aria-labelledby') ||
                      (element.textContent?.trim() ?? '') ||
                      (element as HTMLInputElement).labels?.length;
      
      if (!hasLabel) {
        logger.warn('Missing accessible label', { element: element.tagName, id: element.id });
        passed = false;
      }
    });

    return passed;
  },

  testSemanticHTML: (): boolean => {
    const requiredElements = [
      { selector: 'main', name: 'main landmark' },
      { selector: 'header, [role="banner"]', name: 'banner landmark' },
      { selector: 'h1, h2, h3, h4, h5, h6', name: 'heading structure' }
    ];

    let passed = true;

    requiredElements.forEach(({ selector, name }) => {
      if (!document.querySelector(selector)) {
        console.warn(`Missing ${name}`);
        passed = false;
      }
    });

    return passed;
  }
};

// High contrast mode detection
export const HighContrastMode = {
  isEnabled: (): boolean => {
    return window.matchMedia('(prefers-contrast: high)').matches;
  },

  addListener: (callback: (isHighContrast: boolean) => void) => {
    const mediaQuery = window.matchMedia('(prefers-contrast: high)');
    mediaQuery.addListener((e) => callback(e.matches));
    return () => mediaQuery.removeListener((e) => callback(e.matches));
  }
};

// Reduced motion detection
export const ReducedMotion = {
  isEnabled: (): boolean => {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  },

  addListener: (callback: (isReduced: boolean) => void) => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    mediaQuery.addListener((e) => callback(e.matches));
    return () => mediaQuery.removeListener((e) => callback(e.matches));
  }
};

// Initialize accessibility features
export const initializeAccessibility = () => {
  // Initialize screen reader announcements
  ScreenReaderAnnouncer.init();

  // Add keyboard navigation listeners
  document.addEventListener('keydown', (event) => {
    // ESC key closes modals/dropdowns
    if (event.key === 'Escape') {
      const openMenus = document.querySelectorAll('[aria-expanded="true"]');
      openMenus.forEach(menu => {
        if (menu instanceof HTMLElement) {
          menu.click(); // Close the menu
        }
      });
    }
  });

  // Announce page changes for single-page apps
  let currentPath = window.location.pathname;
  const observer = new MutationObserver(() => {
    if (window.location.pathname !== currentPath) {
      currentPath = window.location.pathname;
      const pageTitle = document.title;
      ScreenReaderAnnouncer.announce(`Navigated to ${pageTitle}`, 'polite');
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  // Log accessibility test results in development
  if (process.env.NODE_ENV === 'development') {
    setTimeout(() => {
      const testResults = AccessibilityTester.runCompleteTest();
      logger.info('Accessibility Test Results', {
        passed: testResults.passed,
        results: testResults.results,
        issues: testResults.issues.length > 0 ? testResults.issues : undefined
      });
    }, 2000);
  }
};